import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { toast } from "react-toastify";
import "../../style/transfertopos.css";

import EyeIcon from "../../assets/icons/EyeIcon.js";
import RedDeleteIcon from "../../assets/icons/RedDeleteIcon.js";
import DeleteModalIcon from "../../assets/delete-modal-icon.svg";
import NoTransferModalIcon from "../../assets/no-imei-modal-icon.svg";
import NoDataIcon from "../../assets/icons/NoDataIcon.js";
import FoundIcon from "../../assets/icons/FoundIcon.js";
import transferVector from "../../assets/transfer-vector.svg";
import TodoListIcon from "../../assets/icons/TodoListIcon.js";
import { useSelector } from "react-redux";
import { stockTransferAPI } from "../../services";

const PAGE_SIZE = 25;

const normalizeTransferItem = (item) => {
  if (!item) return null;

  const transferId =
    item.transfer_id ?? item.id ?? item.transferId ?? item.transfer_number ?? null;
  const transferNumber =
    item.transfer_number ??
    item.transferNumber ??
    (transferId ? `TR-${transferId}` : null);
  const resolvedId =
    transferId ??
    transferNumber ??
    `temp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

  const quantitySource =
    item.quantity ?? item.transfer_quantity ?? item.total_quantity ?? 0;
  const numericQuantity = Number(quantitySource);

  return {
    id: resolvedId,
    transferId: transferId ?? resolvedId,
    transferNumber: transferNumber ?? resolvedId,
    model:
      item.product_name ??
      item.product_model ??
      item.product ??
      item.model ??
      item.model_name ??
      item.productModel ??
      "-",
    quantity: Number.isFinite(numericQuantity) ? numericQuantity : 0,
    createdAt: item.created_at ?? item.createdAt ?? null,
    supplierName:
      item.supplier_name ??
      item.supplier ??
      item.supplierName ??
      item.vendor_name ??
      null,
    raw: item,
  };
};

const extractTransferPayload = (response) => {
  if (!response) {
    return { results: [], count: 0 };
  }

  if (Array.isArray(response)) {
    return { results: response, count: response.length };
  }

  if (Array.isArray(response.results)) {
    return {
      results: response.results,
      count: response.count ?? response.results.length,
    };
  }

  if (Array.isArray(response.data)) {
    return {
      results: response.data,
      count: response.count ?? response.data.length,
    };
  }

  if (Array.isArray(response.data?.results)) {
    return {
      results: response.data.results,
      count: response.data.count ?? response.data.results.length,
    };
  }

  return { results: [], count: 0 };
};

// const normalizeProductOptions = (payload) => {
//   const rawList = Array.isArray(payload?.results)
//     ? payload.results
//     : Array.isArray(payload?.data?.results)
//     ? payload.data.results
//     : Array.isArray(payload?.data)
//     ? payload.data
//     : Array.isArray(payload)
//     ? payload
//     : [];

//   const uniqueMap = new Map();
//   rawList.forEach((item, index) => {
//     if (!item) return;
//     const id =
//       item.id ??
//       item.product_id ??
//       item.productId ??
//       item.uuid ??
//       item.slug ??
//       item.sku ??
//       index;
//     const label =
//       item.name ??
//       item.product_name ??
//       item.model ??
//       item.model_name ??
//       item.title ??
//       `Product ${index + 1}`;
//     const normalizedId = String(id);
//     if (!uniqueMap.has(normalizedId)) {
//       uniqueMap.set(normalizedId, { value: normalizedId, label });
//     }
//   });

//   return Array.from(uniqueMap.values());
// };

/* ---------------- Helper Functions ---------------- */
const formatTransferDate = (dateString) => {
  return new Date(dateString).toLocaleDateString();
};

const calculatePagination = (totalCount, currentPage, pageSize) => {
  const totalPages = Math.ceil(totalCount / pageSize);
  return {
    totalPages,
    hasPrev: currentPage > 1,
    hasNext: currentPage < totalPages
  };
};

/* ---------------- Tooltip Component ---------------- */
// const Tooltip = ({ children, content, show }) => {
//   const [isVisible, setIsVisible] = useState(false);
//   const [position, setPosition] = useState({ top: 0, left: 0 });
//   const triggerRef = useRef(null);
//   const tooltipRef = useRef(null);

//   useEffect(() => {
//     if (isVisible && triggerRef.current && tooltipRef.current) {
//       const triggerRect = triggerRef.current.getBoundingClientRect();
//       const tooltipRect = tooltipRef.current.getBoundingClientRect();
//       const viewportWidth = window.innerWidth;
//       const viewportHeight = window.innerHeight;

//       let top = triggerRect.bottom + 8;
//       let left =
//         triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;

//       // Adjust if tooltip goes off screen
//       if (left < 8) left = 8;
//       if (left + tooltipRect.width > viewportWidth - 8) {
//         left = viewportWidth - tooltipRect.width - 8;
//       }
//       if (top + tooltipRect.height > viewportHeight - 8) {
//         top = triggerRect.top - tooltipRect.height - 8;
//       }

//       setPosition({ top, left });
//     }
//   }, [isVisible]);

//   if (!show) return children;

//   return (
//     <>
//       <span
//         ref={triggerRef}
//         onMouseEnter={() => setIsVisible(true)}
//         onMouseLeave={() => setIsVisible(false)}
//         style={{ cursor: "help" }}
//       >
//         {children}
//       </span>
//       {isVisible && (
//         <div
//           ref={tooltipRef}
//           className="transfer-tooltip"
//           style={{
//             position: "fixed",
//             top: position.top,
//             left: position.left,
//             zIndex: 1000,
//           }}
//         >
//           {content}
//         </div>
//       )}
//     </>
//   );
// };

/* ---------------- utils ---------------- */
// function useClickOutside(ref, onClose) {
//   useEffect(() => {
//     const handler = (e) => {
//       if (!ref.current) return;
//       if (!ref.current.contains(e.target)) onClose?.();
//     };
//     document.addEventListener("mousedown", handler);
//     document.addEventListener("touchstart", handler);
//     return () => {
//       document.removeEventListener("mousedown", handler);
//       document.removeEventListener("touchstart", handler);
//     };
//   }, [ref, onClose]);
// }

/* ---------------- toolbar ---------------- */
const ListToolbar = ({
  search,
  onSearchSubmit,
}) => {
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  const [inputValue, setInputValue] = useState(search);

  useEffect(() => {
    setInputValue(search);
  }, [search]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      onSearchSubmit(inputValue);
    }
  };

  return (
    <div className="transfer-toolbar" style={{ width: "1138px", maxWidth: "100%" }}>
      <div
        className={`${
          isPartsURL
            ? "search-box-parts"
            : isAccessoriesURL
            ? "search-box-acc"
            : "search-box"
        }`}
        style={{ width: "1138px", maxWidth: "100%" }}
      >
        <span className="icon" aria-hidden>
          <Search size={16} color="#8C959F" />
        </span>
        <input
          value={inputValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder="Search by transfer no. or by model..."
          aria-label="Search by transfer no. or by model"
        />
      </div>
    </div>
  );
};

/* ---------------- table ---------------- */
const ListTable = ({ rows, onView, onDelete, onImport, loading }) => {
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  // Ensure rows is always an array
  const safeRows = Array.isArray(rows) ? rows : [];

  return (
    <div className="transfer-table-card">
      <table className="transfer-table">
        <thead>
          <tr>
            <th style={{ width: 200 }}>TRANSFER NO.</th>
            <th style={{ width: 200 }}>MODEL</th>
            <th style={{ width: 200 }}>QUANTITY</th>
            <th style={{ width: 120, textAlign: "center" }}>ACTION</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: 5 }).map((_, index) => (
              <tr key={index}>
                <td><div style={{ height: "16px", backgroundColor: "#f1f5f9", borderRadius: "4px" }} /></td>
                <td><div style={{ height: "16px", backgroundColor: "#f1f5f9", borderRadius: "4px" }} /></td>
                <td><div style={{ height: "16px", backgroundColor: "#f1f5f9", borderRadius: "4px" }} /></td>
                <td><div style={{ height: "16px", backgroundColor: "#f1f5f9", borderRadius: "4px" }} /></td>
              </tr>
            ))
          ) : safeRows.length === 0 ? (
            <tr>
              <td colSpan="4" className="no-transfer-message">
                <div
                  style={{
                    marginBottom: "4rem",
                    marginTop: "4rem",
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <NoDataIcon />
                  <h3
                    style={{
                      marginTop: "1rem",
                      color: "#6E7781",
                      fontSize: "14px",
                      fontWeight: "400",
                    }}
                  >
                    No transfer records found
                  </h3>
                </div>
              </td>
            </tr>
          ) : (
            safeRows.map((r) => (
              <tr key={r.transferId || r.id || r.transferNumber}>
                <td className="muted">{r.transferNumber || r.id || "-"}</td>
                <td>{r.model || "-"}</td>
                <td>
                  {typeof r.quantity === "number"
                    ? r.quantity.toLocaleString()
                    : r.quantity || 0}
                </td>
                <td className="action-buttons">
                  <button
                    title="View"
                    className={`${
                      isPartsURL
                        ? "action-btn-parts view"
                        : isAccessoriesURL
                        ? "action-btn-acc view"
                        : "action-btn view"
                    }`}
                    onClick={() => onView?.(r)}
                  >
                    <EyeIcon fill="currentColor" />
                  </button>
                  <button
                    title="Delete"
                    className="action-btn delete"
                    onClick={() => onDelete(r)}
                  >
                    <RedDeleteIcon />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

/* ---------------- Create Transfer Confirmation Modal ---------------- */
const CreateTransferConfirmModal = ({ open, onClose, onConfirm, loading }) => {
  
  if (!open) return null;

  return (
    <div
      className="transfer-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="transfer-modal"
        role="dialog"
        aria-modal="true"
        style={{ width: "400px", padding: "24px", borderRadius: "12px", background: "#F9FAFB" }}
      >
        <div style={{ marginBottom: "16px" }}>
          <div style={{
            width: "48px",
            height: "48px",
            borderRadius: "10px",
            background: "#EFF8FF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "16px"
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M16 6L20 9L16 12M20 9L4 9" stroke="#1570EF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 18L4 15L8 12M4 15L20 15" stroke="#1570EF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
        
        <h3 style={{ 
          margin: "0 0 4px 0", 
          fontSize: "18px", 
          fontWeight: "600", 
          color: "#101828",
          lineHeight: "28px"
        }}>
          Create a Transfer
        </h3>
        
        <p style={{
          margin: "0 0 24px 0",
          fontSize: "14px",
          color: "#667085",
          lineHeight: "20px"
        }}>
          Are you sure you want to create a Transfer?
        </p>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
          <button 
            type="button" 
            onClick={onClose}
            style={{
              height: "40px",
              padding: "10px 16px",
              borderRadius: "8px",
              border: "1px solid #D0D5DD",
              background: "#ffffff",
              color: "#344054",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              fontFamily: "Inter, sans-serif"
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            style={{
              height: "40px",
              padding: "10px 16px",
              borderRadius: "8px",
              border: "none",
              background: "#1570EF",
              color: "#ffffff",
              fontSize: "14px",
              fontWeight: "600",
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.6 : 1,
              fontFamily: "Inter, sans-serif"
            }}
          >
            {loading ? "Creating..." : "Yes, Create"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Product Dropdown ---------------- */
// const ProductDropdown = ({
//   value,
//   options = [],
//   loading = false,
//   placeholder = "Select or Search Products...",
//   inputId,
//   onChange,
// }) => {
//   const [query, setQuery] = useState("");
//   const [isOpen, setIsOpen] = useState(false);
//   const dropdownRef = useRef(null);

//   useClickOutside(dropdownRef, () => setIsOpen(false));

//   useEffect(() => {
//     if (isOpen) return;
//     const selected = options.find((opt) => opt.value === value);
//     if (selected) {
//       setQuery(selected.label);
//     } else if (!value) {
//       setQuery("");
//     }
//   }, [value, options, isOpen]);

//   const filteredOptions = useMemo(() => {
//     const searchTerm = query.trim().toLowerCase();
//     if (!searchTerm) return options;
//     return options.filter((option) =>
//       option.label.toLowerCase().includes(searchTerm)
//     );
//   }, [query, options]);

//   const handleInputChange = (event) => {
//     const nextQuery = event.target.value;
//     setQuery(nextQuery);
//     if (value) {
//       onChange?.("");
//     }
//     if (!isOpen) {
//       setIsOpen(true);
//     }
//   };

//   const handleSelect = (option) => {
//     setQuery(option.label);
//     onChange?.(option.value);
//     setIsOpen(false);
//   };

//   const renderDropdownBody = () => {
//     if (!isOpen) return null;

//     if (loading) {
//       return (
//         <div className="figma-select-dropdown">
//           <div className="figma-select-option disabled">Loading products...</div>
//         </div>
//       );
//     }

//     if (filteredOptions.length === 0) {
//       return (
//         <div className="figma-select-dropdown">
//           <div className="figma-select-option disabled">No products found</div>
//         </div>
//       );
//     }

//     return (
//       <div className="figma-select-dropdown">
//         {filteredOptions.map((option) => (
//           <button
//             type="button"
//             key={option.value}
//             className={`figma-select-option${option.value === value ? " selected" : ""}`}
//             onClick={() => handleSelect(option)}
//           >
//             {option.label}
//           </button>
//         ))}
//       </div>
//     );
//   };

//   return (
//     <div
//       ref={dropdownRef}
//       className={`figma-select-wrapper searchable${isOpen ? " open" : ""}`}
//     >
//       <Search size={16} className="figma-select-search-icon" />
//       <input
//         id={inputId}
//         type="text"
//         className="figma-select-input"
//         value={query}
//         onChange={handleInputChange}
//         onFocus={(event) => {
//           setIsOpen(true);
//           event.target.select();
//         }}
//         placeholder={placeholder}
//         aria-label="Select or search products"
//         autoComplete="off"
//       />
//       <ChevronDown
//         size={16}
//         className={`figma-select-icon${isOpen ? " open" : ""}`}
//       />
//       {renderDropdownBody()}
//     </div>
//   );
// };

/* ---------------- No Data Found Modal ---------------- */
const NoDataFoundModal = ({ open, onClose, searchTerm }) => {
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  if (!open) return null;

  return (
    <div
      className="transfer-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="delete-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="deleteRecordTitle"
      >
        <div className="modal-icon">
          <img src={NoTransferModalIcon} alt="Delete" />
        </div>
        <h3 className="modal-title" id="deleteRecordTitle">
          Transfer Record Not Found
        </h3>
        <p className="modal-message">
          We couldn't find the scanned transfer record in the system. <br />
          Please check the ID and try again.
        </p>
        <div className="modal-actions">
          <button type="button" className={`modal-ok-btn${isPartsURL ? "-parts" : isAccessoriesURL ? "-acc" : ""}`} onClick={onClose}>
            Ok
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Data Found Modal ---------------- */
const DataFoundModal = ({ open, onClose, searchResult }) => {
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  if (!open || !searchResult) return null;

  const recordId =
    searchResult.transferNumber || searchResult.transferId || searchResult.id || "-";
  const registeredDate = searchResult.createdAt ?? searchResult.raw?.created_at;
  const supplierName =
    searchResult.supplierName ??
    searchResult.raw?.supplier_name ??
    searchResult.raw?.supplier ??
    "-";

  return (
    <div
      className="transfer-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="delete-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dataFoundTitle"
        style={{ padding: "24px", maxWidth: "451px" }}
      >
        <div className="modal-icon">
          <FoundIcon />
        </div>
        <h3 className="modal-title" id="dataFoundTitle">
          Transfer Record Found
        </h3>
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={{ display: "flex", flexDirection: "row", gap: "16px" }}>
            <div
              style={{ fontWeight: "400", fontSize: "14px", color: "#6E7781" }}
            >
              <span>Record ID: </span>
              {/* <span>IMEI : </span> */}
              <span>{recordId}</span>
            </div>
            <div
              style={{ fontWeight: "400", fontSize: "14px", color: "#6E7781" }}
            >
              <span>Date of Registered: </span>
              <span>
                {registeredDate
                  ? formatTransferDate(registeredDate)
                  : "-"}
              </span>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "row" }}>
            <span
              style={{ fontWeight: "400", fontSize: "14px", color: "#6E7781" }}
            >
              Supplier :{" "}
            </span>
            <span
              style={{ fontWeight: "400", fontSize: "14px", color: "#424A53" }}
            >
              {" "}
              {supplierName}
            </span>
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className={`modal-ok-btn${isPartsURL ? "-parts" : isAccessoriesURL ? "-acc" : ""}`} onClick={onClose}>
            Got It
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Delete Modal ---------------- */
const DeleteRecordModal = ({ open, onClose, onConfirm, record }) => {
  if (!open || !record) return null;

  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="delete-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="deleteRecordTitle"
      >
        <div className="modal-icon">
          <img src={DeleteModalIcon} alt="Delete" />
        </div>
        <h3 className="modal-title" id="deleteRecordTitle">
          Delete Transfer
        </h3>
        <p className="modal-message">
          Are you sure you want to delete this transfer? This action cannot be undone.
        </p>
        <div className="modal-actions">
          <button type="button" className="modal-cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="modal-delete-btn" onClick={() => onConfirm(record)}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Page ---------------- */
export default function TransferToPOSList() {
  const navigate = useNavigate();
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  const [search, setSearch] = useState("");
  const [data, setData] = useState({ results: [], count: 0 });
  const [page, setPage] = useState(1);
  const pageSize = PAGE_SIZE;
  // const [, setAddOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [noDataModalOpen, setNoDataModalOpen] = useState(false);
  const [dataFoundModalOpen, setDataFoundModalOpen] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const searchSubmitRef = useRef(false);

  const handleSearchSubmit = (searchValue) => {
    const trimmedValue = searchValue.trim();
    searchSubmitRef.current = Boolean(trimmedValue);
    setSearch(trimmedValue);
    // Reset page to 1 when searching
    setPage(1);
  };

  // Load transfer records from API
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const loadTransfers = async () => {
      setLoading(true);
      const trimmedSearch = search.trim();
      if (!trimmedSearch) {
        searchSubmitRef.current = false;
      }

      try {
        const response = await stockTransferAPI.getTransfers(
          {
            page,
            page_size: pageSize,
            ...(trimmedSearch ? { search: trimmedSearch } : {}),
          },
          { signal: controller.signal }
        );

        if (!isMounted) {
          return;
        }

        const { results: rawResults, count: totalCount } =
          extractTransferPayload(response);
        const normalizedResults = rawResults
          .map((item) => normalizeTransferItem(item))
          .filter(Boolean);

        setData({
          results: normalizedResults,
          count: typeof totalCount === "number" ? totalCount : normalizedResults.length,
        });

        if (searchSubmitRef.current && trimmedSearch) {
          if (normalizedResults.length > 0) {
            setSearchResult(normalizedResults[0]);
            setDataFoundModalOpen(true);
          } else {
            setSearchResult(null);
            setNoDataModalOpen(true);
          }
          searchSubmitRef.current = false;
        }
      } catch (error) {
        if (!isMounted || error.name === "CanceledError") {
          return;
        }
        console.error("Failed to load transfers:", error);
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load transfer records";
        toast.error(message);
        setData({ results: [], count: 0 });
        searchSubmitRef.current = false;
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadTransfers();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [page, pageSize, search, reloadKey]);

  const refresh = () => {
    setReloadKey((prev) => prev + 1);
  };

  const onImportRow = (row) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv,.txt";
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      toast.success(`File ${file.name} selected for import`);
      refresh();
    };
    input.click();
  };

  const onDeleteRow = (row) => {
    setToDelete(row);
    setDeleteOpen(true);
  };

  const confirmDelete = (row) => {
    toast.success(
      `Transfer record ${row.id} deleted successfully`
    );
    setDeleteOpen(false);
    setToDelete(null);
    const targetId = row?.id ?? row?.transferId;
    setData((prevData) => ({
      results: prevData.results.filter(
        (item) => item.id !== targetId && item.transferId !== targetId
      ),
      count: Math.max(prevData.count - 1, 0),
    }));
  };

  // const createRecord = async (formValues) => {
  //   const productIdValue = formValues.productId?.trim();
  //   const sellingPriceValue = formValues.sellingPrice?.trim();
  //   const quantityValue = formValues.quantity?.trim();

  //   const productIdNumber = Number(productIdValue);
  //   const quantityNumber = Number(quantityValue);
  //   const sellingPriceNumber = sellingPriceValue ? Number(sellingPriceValue) : undefined;

  //   if (!Number.isFinite(productIdNumber) || productIdNumber <= 0) {
  //     const error = new Error("Please select a valid product");
  //     toast.error(error.message);
  //     throw error;
  //   }

  //   if (!Number.isFinite(quantityNumber) || quantityNumber <= 0) {
  //     const error = new Error("Please provide a valid quantity");
  //     toast.error(error.message);
  //     throw error;
  //   }

  //   const requestPayload = {
  //     product_id: productIdNumber,
  //     quantity: quantityNumber,
  //   };

  //   if (typeof sellingPriceNumber === "number" && !Number.isNaN(sellingPriceNumber) && sellingPriceNumber > 0) {
  //     requestPayload.selling_price = sellingPriceNumber;
  //   }

  //   try {
  //     await stockTransferAPI.addTransferItem(requestPayload);
  //     toast.success("Transfer created successfully");
  //     setAddOpen(false);
  //     refresh();
  //   } catch (error) {
  //     const message =
  //       error?.response?.data?.message ||
  //       error?.response?.data?.detail ||
  //       error?.message ||
  //       "Failed to create transfer";
  //     toast.error(message);
  //     throw error;
  //   }
  // };

  const paginationInfo = useMemo(
    () => calculatePagination(data.count || 0, page, pageSize),
    [data.count, page, pageSize]
  );

  const totalPages = Math.max(1, paginationInfo.totalPages || 1);
  const totalModels = data?.count || 0;
  const primaryColor = isPartsURL
    ? "var(--primary-color-parts)"
    : isAccessoriesURL
    ? "var(--primary-color-acc)"
    : "var(--primary-color-mobile)";
  const summaryIconBackground = isPartsURL
    ? "rgba(219, 46, 49, 0.12)"
    : isAccessoriesURL
    ? "rgba(114, 191, 72, 0.12)"
    : "rgba(47, 128, 237, 0.12)";

  const handleViewDetails = (record) => {
    const transferId = record?.transferId ?? record?.id;
    if (!transferId) return;
    navigate(`/transfer_to_pos/${transferId}`);
  };

  return (
    <div className="transfer-page">
      <div className="card">
        <div className="transfer-card-top">
          <div className="transfer-card-title">
            <h2>Transfer to POS</h2>
            <p>Move stock from godown to POS for retail or bulk selling</p>
          </div>
          <button
            type="button"
            className={` ${
              isPartsURL
                ? "create-btn-parts"
                : isAccessoriesURL
                ? "create-btn-acc"
                : "create-btn"
            }`}
            onClick={() => setConfirmOpen(true)}
            style={{
              width: "158px",
              height: "44px",
              padding: "10px",
              borderRadius: "6px",
              gap: "6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <img src={transferVector} alt="Transfer" style={{ width: "13.75px", height: "13.75px", filter: "brightness(0) invert(1)" }} />
            <span>Create Transfer</span>
          </button>
        </div>

        <div className="transfer-summary-row">
          <div className={`transfer-summary-card${isPartsURL ? " parts" : isAccessoriesURL ? " acc" : ""}`}>
            <div>
              <p className="transfer-summary-label">Total Models</p>
              <p className="transfer-summary-value">
                {totalModels.toLocaleString()}
              </p>
            </div>
            <div
              className="transfer-summary-icon"
              style={{ color: primaryColor, background: summaryIconBackground }}
            >
              <TodoListIcon fill="currentColor" />
            </div>
          </div>
        </div>

        <ListToolbar
          search={search}
          onSearchSubmit={handleSearchSubmit}
        />

        <ListTable
          rows={data.results}
          onView={handleViewDetails}
          onDelete={onDeleteRow}
          onImport={onImportRow}
          loading={loading}
        />

        {data.count > pageSize && (
          <div className="pagination">
            <span>
              Page {page} of {totalPages}
            </span>
            <div className="pager">
              <button
                type="button"
                disabled={!paginationInfo.hasPrev}
                className="previous"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className={`next${isPartsURL ? "-parts" : isAccessoriesURL ? "-acc" : ""}`}
                disabled={!paginationInfo.hasNext}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <CreateTransferConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        loading={confirmLoading}
        onConfirm={async () => {
          setConfirmLoading(true);
          try {
            await stockTransferAPI.createTransfer();
            toast.success("Transfer created successfully");
            setConfirmOpen(false);
            refresh();
          } catch (error) {
            const message = error?.response?.data?.message || error?.message || "Failed to create transfer";
            toast.error(message);
          } finally {
            setConfirmLoading(false);
          }
        }}
      />

      <DeleteRecordModal
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setToDelete(null);
        }}
        onConfirm={confirmDelete}
        record={toDelete}
      />

      <NoDataFoundModal
        open={noDataModalOpen}
        onClose={() => setNoDataModalOpen(false)}
        searchTerm={search}
      />

      <DataFoundModal
        open={dataFoundModalOpen}
        onClose={() => setDataFoundModalOpen(false)}
        searchResult={searchResult}
      />
    </div>
  );
}